<template>
  <nav :aria-label="ariaLabel" v-bind="getPt('root', passThrough)">
    <!-- PrimeVue's root claims role="tablist"; the steps are a list of buttons, not tabs. -->
    <Stepper :value="value" unstyled role="none">
      <ol
        class="flex flex-col flex-nowrap gap-m"
        v-bind="getPt('list', passThrough)"
      >
        <!-- asChild: PrimeVue's own header would move its highlight before the parent's
             guard decides, and render <button disabled> with tab roles. -->
        <Step
          v-for="item in items"
          :key="item.step.value"
          :value="item.step.value"
          as-child
          unstyled
        >
          <li class="relative">
            <button
              type="button"
              class="group flex flex-nowrap items-start gap-s w-full p-2xs radius-small text-left outline-none transition duration-200 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-primary-border-default"
              :class="
                item.active
                  ? 'cursor-default'
                  : item.navigable
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              "
              :aria-current="item.active ? 'step' : undefined"
              :aria-disabled="item.navigable ? undefined : 'true'"
              :tabindex="item.navigable ? undefined : -1"
              v-bind="
                getPt('step', passThrough, {
                  context: {
                    step: item.step,
                    index: item.index,
                    active: item.active,
                  },
                })
              "
              @click="onStepClick(item)"
            >
              <!-- Font glyphs can't morph, so an icon change cross-fades instead: both glyphs
                   share the one grid cell while the old spins out and the new springs in. -->
              <span
                class="grid place-items-center shrink-0 w-2xl h-2xl radius-full border transition duration-200 motion-reduce:transition-none"
                :class="item.indicatorClass"
              >
                <Transition name="meridia-stepper-icon">
                  <MeridiaIcon
                    :key="item.icon"
                    :name="item.icon"
                    type="material-symbols-outlined"
                    class="col-start-1 row-start-1 icon-s leading-none"
                    aria-hidden="true"
                  />
                </Transition>
              </span>
              <!-- pt-[7px] centres the first 18px title line on the 32px indicator. The prefix
                   hangs: wrapped label lines and the caption share the label's left edge.
                   Poppins ignores tabular-nums, so the prefix gets a min width that fits the
                   widest "N." for 1-9 ("4." is 12.5px) and every label starts at the same x. -->
              <span class="flex flex-nowrap gap-2xs min-w-0 pt-[7px]">
                <span
                  class="shrink-0 min-w-[13px] tabular-nums text-caption-large leading-[18px] uppercase transition duration-200 motion-reduce:transition-none"
                  :class="item.titleClass"
                  >{{ item.index + 1 }}.</span
                >
                <span class="flex flex-col flex-nowrap min-w-0">
                  <span
                    class="text-caption-large leading-[18px] uppercase break-words transition duration-200 motion-reduce:transition-none"
                    :class="item.titleClass"
                    >{{ item.step.label }}</span
                  >
                  <!-- The 0fr → 1fr grid row animates the caption's auto height, so the rows
                       below slide instead of jumping when a step turns (un)available. -->
                  <Transition name="meridia-stepper-caption">
                    <span
                      v-if="item.step.caption"
                      class="meridia-stepper-caption grid"
                    >
                      <span
                        class="min-h-0 overflow-hidden text-body-large break-words"
                        :class="item.captionClass"
                        v-bind="getPt('caption', passThrough)"
                        >{{ item.step.caption }}</span
                      >
                    </span>
                  </Transition>
                </span>
                <span class="sr-only">{{ stateLabels[item.step.state] }}</span>
              </span>
            </button>
            <span
              v-if="item.index < steps.length - 1"
              aria-hidden="true"
              class="absolute left-[19.5px] top-[40px] bottom-[-16px] w-px transition duration-200 motion-reduce:transition-none"
              :class="item.connectorClass"
            />
          </li>
        </Step>
      </ol>
    </Stepper>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Stepper from 'primevue/stepper';
import Step from 'primevue/step';
import { usePassThrough } from 'primevue/passthrough';
import {
  StepperStepState,
  type MeridiaStepperProps,
  type MeridiaStepperStep,
} from './types';
import MeridiaIcon from '../icon/MeridiaIcon.vue';
import { getPt } from '../utils';

type StepItem = {
  step: MeridiaStepperStep;
  index: number;
  active: boolean;
  navigable: boolean;
  icon: string;
  indicatorClass: string;
  titleClass: string;
  captionClass: string;
  connectorClass: string;
};

const props = defineProps<MeridiaStepperProps>();

const emit = defineEmits<{
  /** A navigable, non-active step was activated. The stepper does not move until `value` changes. */
  (event: 'update:value', value: number): void;
}>();

const INDICATOR_CLASSES: Record<`${StepperStepState}`, string> = {
  [StepperStepState.INCOMPLETE]:
    'bg-neutral-surface-default border-neutral-border-default text-neutral-text-subtitle',
  [StepperStepState.COMPLETE]:
    'bg-primary-surface-muted border-primary-border-subtle text-primary-label-elevated',
  [StepperStepState.INVALID]:
    'bg-critical-surface-disabled border-critical-border-default text-critical-label-darker',
  [StepperStepState.LOCKED]:
    'bg-neutral-surface-muted border-neutral-border-default text-neutral-text-subtitle',
  [StepperStepState.UNAVAILABLE]:
    'bg-neutral-surface-muted border-dashed border-neutral-border-disabled text-neutral-text-caption',
};
const ACTIVE_INDICATOR_CLASS =
  'bg-primary-surface-default border-primary-border-default text-primary-label-elevated ring-4 ring-primary-surface-muted';
const ACTIVE_INVALID_RING_CLASS = 'ring-4 ring-critical-surface-subtle';
// Hover recolours instead of filling the row: a navigable step darkens towards primary.
const HOVER_INDICATOR_CLASS =
  'group-hover:border-primary-border-darker group-hover:text-primary-label-darker';
const HOVER_INVALID_INDICATOR_CLASS =
  'group-hover:border-critical-border-darker';
const HOVER_TITLE_CLASS = 'group-hover:text-primary-label-darker';

// No section defaults here: usePassThrough spreads defaults over the consumer's sections and
// getPt reads them unmerged, so defaults would drop the consumer's attributes. Classes live in
// the template, where Vue merges them with the pass-through.
const passThrough = computed(() =>
  usePassThrough(props.pt, {}, { mergeSections: true, mergeProps: true })
);

const items = computed<StepItem[]>(() =>
  props.steps.map((step, index) => {
    const active = step.value === props.value;
    const navigable =
      !step.disabled && step.state !== StepperStepState.UNAVAILABLE;
    const hoverable = navigable && !active;
    const invalid = step.state === StepperStepState.INVALID;
    const indicatorHover = invalid
      ? HOVER_INVALID_INDICATOR_CLASS
      : HOVER_INDICATOR_CLASS;
    const titleHover = invalid ? '' : HOVER_TITLE_CLASS;
    return {
      step,
      index,
      active,
      navigable,
      icon: getIndicatorIcon(step, active),
      indicatorClass: hoverable
        ? `${getIndicatorClass(step, active)} ${indicatorHover}`
        : getIndicatorClass(step, active),
      titleClass: hoverable
        ? `${getTitleClass(step, active, navigable)} ${titleHover}`
        : getTitleClass(step, active, navigable),
      // Secondary to the title, so it stays a shade lighter in every state.
      captionClass:
        step.state === StepperStepState.UNAVAILABLE
          ? 'text-neutral-text-caption'
          : 'text-neutral-text-subtitle',
      connectorClass:
        step.state === StepperStepState.COMPLETE ||
        step.state === StepperStepState.LOCKED
          ? 'bg-primary-border-default'
          : 'bg-neutral-border-default',
    };
  })
);

function getIndicatorIcon(step: MeridiaStepperStep, active: boolean): string {
  if (step.state === StepperStepState.INVALID) return 'priority_high';
  if (step.state === StepperStepState.UNAVAILABLE) return 'block';
  if (active) return step.icon;
  if (step.state === StepperStepState.LOCKED) return 'lock';
  if (step.state === StepperStepState.COMPLETE) return 'check';
  return step.icon;
}

function getIndicatorClass(step: MeridiaStepperStep, active: boolean): string {
  if (step.state === StepperStepState.INVALID) {
    return active
      ? `${INDICATOR_CLASSES.invalid} ${ACTIVE_INVALID_RING_CLASS}`
      : INDICATOR_CLASSES.invalid;
  }
  if (active && step.state !== StepperStepState.UNAVAILABLE)
    return ACTIVE_INDICATOR_CLASS;
  return INDICATOR_CLASSES[step.state];
}

function getTitleClass(
  step: MeridiaStepperStep,
  active: boolean,
  navigable: boolean
): string {
  if (step.state === StepperStepState.INVALID)
    return 'text-critical-label-darker';
  if (step.state === StepperStepState.UNAVAILABLE)
    return 'text-neutral-text-caption';
  if (active) return 'text-neutral-text-title';
  if (!navigable && step.state === StepperStepState.INCOMPLETE)
    return 'text-neutral-text-subtitle';
  return 'text-neutral-text-body';
}

function onStepClick(item: StepItem) {
  if (item.navigable && !item.active) emit('update:value', item.step.value);
}
</script>

<style lang="scss" scoped>
.meridia-stepper-caption {
  grid-template-rows: 1fr;
}

.meridia-stepper-caption-enter-active,
.meridia-stepper-caption-leave-active {
  transition: grid-template-rows 200ms ease-out, opacity 200ms ease-out;
}

.meridia-stepper-caption-enter-from,
.meridia-stepper-caption-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

// Overshooting ease on the way in reads as a spring; the blur blends the two glyphs mid-swap.
.meridia-stepper-icon-enter-active {
  transition: transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 200ms ease-out, filter 200ms ease-out;
}

.meridia-stepper-icon-leave-active {
  transition: transform 200ms ease-in, opacity 200ms ease-in,
    filter 200ms ease-in;
}

.meridia-stepper-icon-enter-from {
  opacity: 0;
  filter: blur(2px);
  transform: scale(0.3) rotate(-90deg);
}

.meridia-stepper-icon-leave-to {
  opacity: 0;
  filter: blur(2px);
  transform: scale(0.3) rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .meridia-stepper-caption-enter-active,
  .meridia-stepper-caption-leave-active,
  .meridia-stepper-icon-enter-active,
  .meridia-stepper-icon-leave-active {
    transition: none;
  }
}
</style>
